import { RepositoryInput, type ParsedGitInfo } from "@/components/RepositoryInput";
import { type GitConfig } from "@/utils/git-config";
import { EnvironmentClassOrderedList } from "@/components/EnvironmentClassOrderedList";
import {
    AuthenticationModal,
    type SCMAuthenticationModalProps,
} from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Button } from "@/components/flexkit/Button";
import { Alert } from "@/components/podkit/alert/Alert";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";

import {
    convertToProjectEnvironmentClass,
    useEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateProject, useUpdateProjectEnvironmentClasses } from "@/queries/project-queries";
import { useIsUserAuthenticatedWithRunner } from "@/queries/runner-queries";
import { EnvironmentCreateError } from "@/routes/environments/create/components/EnvironmentCreateError";
import { localEnvType } from "@/utils/environment-classes";
import { getPreferredLocalEnvironmentClass } from "@/utils/test/api/environment-classes";
import { ConnectError } from "@connectrpc/connect";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { ParseContextURLPreconditionFailureDetails } from "gitpod-next-api/gitpod/v1/runner_pb";
import { getScmProviderName } from "frontend-shared/local-runner";
import { useCallback, useEffect, useMemo, useState, type FC, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

export const CreateProjectModal: FC<{ onClose: () => void }> = ({ onClose }) => {
    const navigate = useNavigate();
    const { toast } = useToast();

    // Repository input state
    const [repositoryUrl, setRepositoryUrl] = useState("");
    const [repositoryUrlValid, setRepositoryUrlValid] = useState(false);
    const [gitConfig, setGitConfig] = useState<GitConfig | null>(null);
    const [parsedInfo, setParsedInfo] = useState<ParsedGitInfo | null>(null);

    // State for parsing status
    const [isLoadingParseContext, setIsLoadingParseContext] = useState(false);
    const [isSuccessParseContext, setIsSuccessParseContext] = useState(false);
    const [parseContextFailed, setParseContextFailed] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [isInvalidArgument, setIsInvalidArgument] = useState(false);
    const [errorParseContext, setErrorParseContext] = useState<Error | null>(null);
    const [isTypingRepositoryUrl, setIsTypingRepositoryUrl] = useState(false);

    const { environmentClassEntries: unfilteredEnvironmentClassEntries, isLoading: isLoadingEnvironmentClassEntries } =
        useEnvironmentClassEntries();
    const [selectedEnvironmentClassEntries, setSelectedEnvironmentClassEntries] = useState<EnvironmentClassEntry[]>([]);

    const allEnvironmentClassEntries = useMemo(() => {
        const localRunner = unfilteredEnvironmentClassEntries.find((e) => e.localRunner);

        // filter out local runners of the current user
        const filtered = unfilteredEnvironmentClassEntries.filter((e) => !e.localRunner);

        // re-add a synthetic local runner
        if (localRunner) {
            localEnvType.policyRestricted = localRunner.policyRestricted;
            return [localEnvType, ...filtered];
        }
        return filtered;
    }, [unfilteredEnvironmentClassEntries]);

    const [projectName, setProjectName] = useState("");
    const [createError, setCreateError] = useState<Error | null>(null);
    const createProject = useCreateProject();
    const updateProjectEnvironmentClasses = useUpdateProjectEnvironmentClasses();

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    // Debounce the repository URL to prevent too many API calls while typing
    const [debouncedRepo] = useDebounce({ repositoryUrl, repositoryUrlValid }, 500, {
        equalityFn: (left, right) =>
            left.repositoryUrl === right.repositoryUrl && left.repositoryUrlValid === right.repositoryUrlValid,
    });

    // Use the first selected environment class for parsing context URL
    const firstSelectedEnvironmentClass: EnvironmentClassEntry | undefined = selectedEnvironmentClassEntries.length
        ? selectedEnvironmentClassEntries[0]
        : undefined;

    // Get the runner ID for parsing
    const actualRunnerId = firstSelectedEnvironmentClass?.localRunner
        ? getPreferredLocalEnvironmentClass(unfilteredEnvironmentClassEntries)?.runner?.runnerId
        : firstSelectedEnvironmentClass?.runner?.runnerId;

    // Authentication check for repository access
    const {
        data: authCheckResponse,
        error: errorAuthCheck,
        refetch: refetchAuthCheck,
    } = useIsUserAuthenticatedWithRunner(actualRunnerId, debouncedRepo.repositoryUrl, {
        refetchUntilAuthenticated: true,
        enabled: debouncedRepo.repositoryUrlValid && !gitConfig,
    });

    // Handle errors
    const isAuthenticationRequired = parseContextFailed || authCheckResponse?.type === "AuthenticationRequired";

    // Get the SCM name from the error details
    const scmName = useMemo(() => {
        let scmID = "";
        if (isAuthenticationRequired) {
            if (parseContextFailed) {
                if (errorParseContext instanceof ConnectError) {
                    const details = errorParseContext.findDetails(ParseContextURLPreconditionFailureDetails);
                    for (const detail of details) {
                        if (detail.scmId) {
                            scmID = detail.scmId;
                        }
                    }
                }
            } else if (authCheckResponse?.type == "AuthenticationRequired") {
                return authCheckResponse.scmName;
            }
        }

        return getScmProviderName(scmID);
    }, [authCheckResponse, errorParseContext, isAuthenticationRequired, parseContextFailed]);

    // Calculate the SCM settings link
    const scmSettingsLink = useMemo(() => {
        if (!firstSelectedEnvironmentClass) {
            return undefined;
        }

        // Remote runners have their own settings page. Local runners are configured on an org level in the runner settings.
        return firstSelectedEnvironmentClass.runner.kind === RunnerKind.REMOTE
            ? `/settings/runners/${firstSelectedEnvironmentClass.runner.runnerId}`
            : "/settings/runners";
    }, [firstSelectedEnvironmentClass]);

    // Set default project name from our parsed context
    useEffect(() => {
        if (repositoryUrlValid && !projectName && parsedInfo?.repo) {
            setProjectName(parsedInfo.repo);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- don't run on name change, because that would mean that it's not possible for the user to clear the input field
    }, [repositoryUrlValid, parsedInfo]);

    const [showScmAuthModal, setShowScmAuthModal] = useState<SCMAuthenticationModalProps | undefined>();

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!projectName || !repositoryUrl || !repositoryUrlValid || selectedEnvironmentClassEntries.length === 0) {
                return;
            }

            // Check if we have parsed context data
            if (!gitConfig && !parseContextFailed) {
                toast({
                    title: "Validating repository",
                    description: "Please wait while we validate the repository URL.",
                });
                return;
            }
            if (!gitConfig) {
                return;
            }

            try {
                setCreateError(null);
                const project = await createProject.mutateAsync({
                    name: projectName,
                    contextURL: repositoryUrl,
                    gitConfig: {
                        remoteUrl: gitConfig.remoteUri,
                        ref: gitConfig.cloneTarget,
                        upstreamRemoteUrl: gitConfig.upstreamRemoteUri,
                        checkoutLocation: gitConfig.checkoutLocation,
                    },
                });

                await updateProjectEnvironmentClasses.mutateAsync({
                    projectId: project.id,
                    projectEnvironmentClasses: selectedEnvironmentClassEntries.map((entry, index) =>
                        convertToProjectEnvironmentClass(entry, index),
                    ),
                });

                toast({
                    title: "Project created",
                    description: `Project "${projectName}" has been created successfully.`,
                });

                onClose();
                navigate(`/projects/${project.id}`);
            } catch (error) {
                setCreateError(error instanceof Error ? error : new Error(String(error)));
                toast({
                    title: "Failed to create project",
                    description: error instanceof Error ? error.message : "An unknown error occurred",
                });
            }
        },
        [
            projectName,
            repositoryUrl,
            repositoryUrlValid,
            selectedEnvironmentClassEntries,
            gitConfig,
            parseContextFailed,
            toast,
            createProject,
            updateProjectEnvironmentClasses,
            onClose,
            navigate,
        ],
    );
    const handleAuthenticate = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            if (!firstSelectedEnvironmentClass || !debouncedRepo.repositoryUrl) {
                return;
            }

            event.preventDefault();
            if (authCheckResponse?.type == "AuthenticationRequired") {
                setShowScmAuthModal({
                    repoURL: debouncedRepo.repositoryUrl,
                    runnerId: firstSelectedEnvironmentClass.runner.runnerId,
                    authResponse: authCheckResponse,
                    onClose: () => setShowScmAuthModal(undefined),
                    onAuthSuccess: () => {
                        setShowScmAuthModal(undefined);
                        void refetchAuthCheck();
                    },
                    "data-track-location": TrackLocations.CreateProjectModal,
                });

                return;
            }
        },
        [firstSelectedEnvironmentClass, debouncedRepo.repositoryUrl, authCheckResponse, refetchAuthCheck],
    );

    const showIsVerifying =
        !parseContextFailed &&
        debouncedRepo.repositoryUrlValid &&
        isLoadingParseContext &&
        !!firstSelectedEnvironmentClass;
    const showAccessSettingsButton = !isTypingRepositoryUrl && isInvalidArgument && !!scmSettingsLink;

    // Form is valid if we have all required fields and either successful context parsing or auth failure
    const isFormValid =
        projectName &&
        debouncedRepo.repositoryUrl &&
        debouncedRepo.repositoryUrlValid &&
        selectedEnvironmentClassEntries.length > 0 &&
        (isSuccessParseContext || parseContextFailed) &&
        !isNotFound;

    const createButtonText = parseContextFailed ? "Authenticate" : "Create";

    return (
        <Dialog open onOpenChange={onOpenChange}>
            {/* we don't use DialogContent component because it uses css transform for centering and it breaks dnd behavior */}
            <ExceptionErrorBoundary>
                <DialogPortal>
                    <DialogOverlay />
                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                        <DialogPrimitive.Content
                            className={cn(
                                "max-h-[90%] w-[500px] max-w-[500px]",
                                "flex flex-col overflow-x-auto rounded-xl border-0.5 border-border-base bg-surface-01/75 p-6 shadow-modal",
                                "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]",
                            )}
                            data-track-location={TrackLocations.CreateProjectModal}
                        >
                            <DialogHeader>
                                <DialogTitle>Create a project</DialogTitle>
                                <DialogDescription>
                                    Create a project to simplify environment creation and enable your team to
                                    collaborate more effectively.{" "}
                                    <ExternalLink href="https://ona.com/docs/ona/projects/overview" iconSize="sm">
                                        Learn more about projects in Ona.
                                    </ExternalLink>
                                </DialogDescription>
                            </DialogHeader>

                            <DialogBody>
                                <form
                                    id="create-project-form"
                                    onSubmit={isAuthenticationRequired ? handleAuthenticate : handleSubmit}
                                    className="flex flex-col gap-1"
                                >
                                    <RepositoryInput
                                        mode="create"
                                        label="Repository URL"
                                        value={repositoryUrl}
                                        onChange={setRepositoryUrl}
                                        onValidationChange={setRepositoryUrlValid}
                                        onGitConfigChange={setGitConfig}
                                        onParsedInfoChange={setParsedInfo}
                                        onParsingStateChange={(state) => {
                                            setIsLoadingParseContext(state.isLoading);
                                            setIsSuccessParseContext(state.isSuccess);
                                            setErrorParseContext(state.error);
                                            setIsNotFound(state.isNotFound);
                                            setIsInvalidArgument(state.isInvalidArgument);
                                            setParseContextFailed(state.parseContextFailed);
                                            setIsTypingRepositoryUrl(state.isTypingUrl);
                                        }}
                                        name="repositoryUrl"
                                        disabled={false}
                                        runnerId={actualRunnerId}
                                        reserveErrorSpace={true}
                                    />

                                    <div className="flex flex-col gap-2 pb-6">
                                        <Label htmlFor="project-name" className="font-medium">
                                            Project name
                                        </Label>
                                        <Input
                                            id="project-name"
                                            data-testid="project-name-input"
                                            name="projectName"
                                            value={projectName}
                                            onChange={(e) => setProjectName(e.target.value)}
                                            placeholder="My Project"
                                            required={!isAuthenticationRequired} // if we need authentication, we don't want to require the project name to proceed
                                        />
                                    </div>

                                    <EnvironmentClassOrderedList
                                        label="Environment Classes"
                                        environmentClassEntries={selectedEnvironmentClassEntries}
                                        allEnvironmentClassEntries={allEnvironmentClassEntries}
                                        onChange={setSelectedEnvironmentClassEntries}
                                        loading={isLoadingEnvironmentClassEntries}
                                        disabled={!debouncedRepo.repositoryUrlValid}
                                        minItems={0}
                                        maxItems={30}
                                    />

                                    {selectedEnvironmentClassEntries.some((entry) => entry.localRunner) &&
                                        debouncedRepo.repositoryUrlValid && ( // we check for repositoryUrlValid to only show the alert when the user is able to choose the environment class (see disabled prop on EnvironmentClassOrderedList)
                                            <Alert className="items-center gap-4 border-0 bg-surface-02">
                                                <span className="font-bold">Local runner selected. </span> <br />
                                                This project will be created with the{" "}
                                                <span className="font-bold">Ona Desktop</span> runner and will open with
                                                the corresponding local runner of each user.
                                            </Alert>
                                        )}

                                    <div
                                        className="flex min-h-4 flex-col items-center justify-center gap-2 text-sm"
                                        data-testid="errors"
                                    >
                                        {showIsVerifying && (
                                            <span data-testid="verifying">Verifying repository and connection...</span>
                                        )}

                                        {parseContextFailed && (
                                            <span className="max-w-80 text-wrap">
                                                <span className="font-bold">
                                                    {firstSelectedEnvironmentClass?.runner?.name || "Unknown runner"}
                                                </span>{" "}
                                                requires authentication with{" "}
                                                <span className="font-bold">{scmName}</span>
                                            </span>
                                        )}

                                        {isNotFound && (
                                            <span>
                                                The repository could not be found. Please check the URL and try again.
                                            </span>
                                        )}

                                        {!showIsVerifying &&
                                            !parseContextFailed &&
                                            !isNotFound &&
                                            !isTypingRepositoryUrl &&
                                            (errorParseContext || errorAuthCheck) && (
                                                // fallback case is just for type safety
                                                <EnvironmentCreateError
                                                    error={
                                                        errorParseContext ??
                                                        errorAuthCheck ??
                                                        new Error("Unknown error")
                                                    }
                                                />
                                            )}

                                        {createError && (
                                            <span className="text-sm">
                                                {createError instanceof ConnectError
                                                    ? createError.rawMessage
                                                    : createError.toString()}
                                            </span>
                                        )}
                                    </div>
                                </form>
                            </DialogBody>

                            <DialogFooter>
                                <DialogClose asChild>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={onClose}
                                        data-tracking-id="cancel-create-project-modal"
                                    >
                                        Cancel
                                    </Button>
                                </DialogClose>

                                {showAccessSettingsButton && (
                                    <Button variant="secondary" type="button" asChild>
                                        <Link
                                            to={scmSettingsLink ?? ""}
                                            onClick={onClose}
                                            data-testid="open-repository-settings-link"
                                            data-tracking-id="open-repository-access-settings-create-project-modal"
                                        >
                                            Open repository access settings
                                        </Link>
                                    </Button>
                                )}

                                {!isAuthenticationRequired ? (
                                    <Button
                                        type="submit"
                                        form="create-project-form"
                                        variant="primary"
                                        disabled={
                                            !isFormValid ||
                                            createProject.isPending ||
                                            updateProjectEnvironmentClasses.isPending ||
                                            showIsVerifying
                                        }
                                        loading={createProject.isPending}
                                        className={cn(showAccessSettingsButton && "hidden")}
                                        data-tracking-id="create-project-create-project-modal"
                                    >
                                        {createButtonText}
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        form="create-project-form"
                                        variant="primary"
                                        className={cn(showAccessSettingsButton && "hidden")}
                                        data-tracking-id="authenticate-scm-create-project-modal"
                                    >
                                        Authenticate
                                    </Button>
                                )}
                                {showScmAuthModal && <AuthenticationModal {...showScmAuthModal} />}
                            </DialogFooter>
                        </DialogPrimitive.Content>
                    </div>
                </DialogPortal>
            </ExceptionErrorBoundary>
        </Dialog>
    );
};
